import { useAuth0 } from "@auth0/auth0-react";
import { useLayoutEffect } from 'react'
import { Routes, Route, useLocation } from 'react-router-dom'
import './App.css'
import './assets/css/utilities.css'
import './assets/css/rc-forms.scss'


// COMPONENTS
import Header from './components/Header/Header'
import Footer from './components/Footer/Footer'
import OnboardingChecklist from "./components/OnboardingChecklist/OnboardingChecklist";
import IconPreview from "./pages/IconPreview";
import EngagementMessageTester from "./components/testing/EngagementMessageTester";

// CORE PAGES
import HomePage from './pages/HomePage'
import Dashboard from './pages/Dashboard'
import Support from "./pages/Support";
import HelpCenter from "./pages/HelpCenter";

// TRANSACTIONS
import Transactions from './pages/Transactions'
import Transaction from './pages/Transaction'
import TransactionCreate from './pages/TransactionCreate'
import TransactionException from './pages/TransactionException'
import TransactionSuggestions from './pages/TransactionSuggestions'
import ManageExceptions from './pages/TransactionExceptions'

// ACCOUNTS
import Account from './pages/Account'
import AccountOB from './pages/AccountOB'
import AccountCreate from './pages/AccountCreate'
import Accounts from './pages/Accounts'

// TOOLS
import PayoffCalculator from './pages/PayoffCalculator'
import PaycheckCalculator from './pages/PaycheckCalculator';
import CreditCardPaymentCalculator from './pages/CreditCardPaymentCalculator'
import RefinanceCalculator from './pages/RefinanceCalculator'
import Snowball from './pages/Snowball'
import SnowballSchedule from "./pages/SnowballSchedule";

// CONTEXT PROVIDERS
import { InputVarsProvider } from './contexts/input-vars-context'
import { UserProvider } from "./contexts/user-context";
import { TransactionDisplayProvider } from "./contexts/transaction-display-context";

// AUTH0
import { AuthenticationGuard } from "./components/Auth/AuthenticationGuard";
import { CallbackPage } from "./pages/CallbackPage";
import { PageLoader } from "./components/Auth/PageLoader";
import { ProfilePage } from "./pages/ProfilePage";
import { Login } from "./pages/Login";
import ErrorPage from "./pages/Error";

// OPEN BANKING
import OpenBankingIntegration from "./pages/OpenBankingIntegration";
import PlaidIntegration from "./pages/PlaidIntegration";
import { OBCustomerProvider } from "./contexts/ob-customer-context";
import { ResponseProvider } from "./hooks/useResponseMessage";

const App = () => {
    const { isLoading } = useAuth0();
    const location = useLocation()

    useLayoutEffect(() => {
        window.scrollTo(0, 0)
    }, [location])

    if (isLoading) {
        return (
            <div className="page-layout">
                <PageLoader />
            </div>
        );
    }

    return (
        <AppProviders>
            <Header />

            <div className={`main-well`}>
                <OnboardingChecklist />
                
                <div className="main-well-content">
                    <RouteList />
                </div>
            </div>

            <Footer />
        </AppProviders>
    )
}

const RouteList = () => (
    <Routes>

        {/* PUBLIC ROUTES */}

        <Route
            path="/" exact
            element={<HomePage />}
        />
        <Route
            path="/support"
            element={<Support />}
        />
        <Route
            path="/help-center"
            element={<HelpCenter />}
        />
        <Route
            path="/tools/payoff-calculator"
            element={<PayoffCalculator />}
        />
        <Route
            path="/tools/credit-card-payment-calculator"
            element={<CreditCardPaymentCalculator />}
        />
        <Route
            path="/tools/refinance-calculator"
            element={<RefinanceCalculator />}
        />
        <Route
            path="/tools/paycheck-calculator"
            element={<PaycheckCalculator />}
        />

        {/* TEMP */}

        <Route
            path="/open-banking-integration"
            element={<AuthenticationGuard component={OpenBankingIntegration} />}
        />
        <Route
            path="/plaid-integration"
            element={<AuthenticationGuard component={PlaidIntegration} />}
        />


        {/* AUTH0 ROUTES */}

        <Route
            path="/callback"
            element={<CallbackPage />}
        />
        <Route
            path="/profile"
            element={<AuthenticationGuard component={ProfilePage} />}
        />
        <Route
            path="/login"
            element={<Login />}
        />
        <Route
            path="/error"
            element={<ErrorPage />}
        />

        {/* PROTECTED ROUTES */}

        <Route
            path="/dashboard"
            element={<AuthenticationGuard component={Dashboard} />}
        />
        <Route
            path="/add-transaction"
            element={<AuthenticationGuard component={TransactionCreate} />}
        />
        <Route
            path="/transaction/:id"
            element={<AuthenticationGuard component={Transaction} />}
        />
        <Route
            path="/exception/:id/:instance"
            element={<AuthenticationGuard component={TransactionException} />}
        />
        <Route
            path="/transactions"
            element={<AuthenticationGuard component={Transactions} />}
        />
        <Route
            path="/transaction-suggestions"
            element={<AuthenticationGuard component={TransactionSuggestions} />}
        />
        <Route
            path="/exceptions"
            element={<AuthenticationGuard component={ManageExceptions} />}
        />
        <Route
            path="/add-account"
            element={<AuthenticationGuard component={AccountCreate} />}
        />
        <Route
            path="/account/:id"
            element={<AuthenticationGuard component={Account} />}
        />
        <Route
            path="/account-connected/:id"
            element={<AuthenticationGuard component={AccountOB} />}
        />
        <Route
            path="/accounts"
            element={<AuthenticationGuard component={Accounts} />}
        />
        <Route
            path="/tools/debt-snowball"
            element={<AuthenticationGuard component={Snowball} />}
        />
        <Route
            path="/tools/snowball-schedule"
            element={<AuthenticationGuard component={SnowballSchedule} />}
        />
        
        {/* DEV ROUTES */}
        <Route
            path="/icon-preview"
            element={<IconPreview />}
        />
        <Route
            path="/test/engagement-messages"
            element={<EngagementMessageTester />}
        />
    </Routes>
)


const AppProviders = ({
    children
}) => (
    <ResponseProvider>
        <UserProvider>
            <InputVarsProvider>
                <TransactionDisplayProvider>
                    <OBCustomerProvider>
                        {children}
                    </OBCustomerProvider>
                </TransactionDisplayProvider>
            </InputVarsProvider>
        </UserProvider>
    </ResponseProvider>
)

export default App
